import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';
import { Table } from '@components/Common';
import { formatLocalDateTime } from '@helpers/formatting';
import { useAdminFilters } from '@hooks/useAdminFilters';
import AdminFiltersControlled, { filterItemsByDateTime } from './AdminFiltersControlled';
import AdminNavigation from './AdminNavigation';

// TanStack Query hooks
import {
  useFlowMetersQuery,
  FlowMeter,
  FlowMeterProductType
} from '@api/queries/flowMeters';

const FlowMetersPage: React.FC = () => {
  const { t } = useTranslation();

  // URL-based filters
  const { filters, dateFilter, setDateFilter } = useAdminFilters();

  // TanStack Query for data fetching
  const { data: items = [], isLoading, refetch } = useFlowMetersQuery(filters.startDate);

  const filteredItems = useMemo(() => filterItemsByDateTime(items, x => x.receivedAt, dateFilter), [items, dateFilter]);

  const columns: Column[] = [
    {
      Header: t('admin.received_at'),
      id: 'receivedAt',
      accessor: 'receivedAt',
      Cell: ({ value }) => formatLocalDateTime(value, true),
      show: true,
      maxWidth: 170,
      filterable: false
    },
    {
      Header: t('entry.terminal'),
      id: 'terminalName',
      maxWidth: 150,
      accessor: 'terminalName'
    },
    {
      Header: t('admin.register'),
      id: 'register',
      accessor: 'register',
      show: true,
      maxWidth: 200
    },
    {
      Header: t('admin.start_date'),
      id: 'startDate',
      accessor: 'startDate',
      Cell: ({ value }) => formatLocalDateTime(value, true),
      show: true,
      maxWidth: 170,
      filterable: false
    },
    {
      Header: t('admin.end_date'),
      id: 'endDate',
      accessor: 'endDate',
      Cell: ({ value }) => formatLocalDateTime(value, true),
      show: true,
      maxWidth: 170,
      filterable: false
    },
    {
      Header: () => (
        <span>
          {t('admin.total_weight')} <sup>kg</sup>
        </span>
      ),
      id: 'totalWeight',
      maxWidth: 120,
      filterable: true,
      accessor: 'totalWeight',
      // tslint:disable-next-line: radix
      filterMethod: (filter, row) => typeof row[filter.id] === 'number' && row[filter.id] === parseInt(filter.value),
      Cell: ({ value }) => (value ? value.toLocaleString('fi') : '')
    },
    {
      Header: t('admin.components'),
      id: 'components',
      accessor: (item: FlowMeter) =>
        item.components
          .map(
            c => `${c.weight.toLocaleString('fi')} kg / ${c.tankName} ${c.temperature ? `/ ${c.temperature} °C` : ''}`
          )
          .join('\n'),
      sortable: false,
      filterable: true,
      Cell: ({ value }) => value.split('\n').map((c: string, i: number) => <div key={i}>{c}</div>),
      show: true,
      width: 250
    },
    {
      Header: t('entry.product'),
      id: 'product',
      accessor: (item: FlowMeter) =>
        (`${item.productName || ''}` + (item.productCode ? ` (${item.productCode})` : '')).trim()
    },
    {
      Header: t('admin.flow_meter_product_type'),
      id: 'type',
      accessor: 'type',
      show: true,
      maxWidth: 200,
      Cell: ({ value }) => {
        return t(`admin.flow_meter_types.${FlowMeterProductType[value].toLowerCase()}`);
      },
      filterMethod: (filter, row) => (filter.value !== '' ? row[filter.id] === Number(filter.value) : true),
      Filter: ({ filter, onChange }) => (
        <select
          onChange={event => onChange(event.target.value)}
          style={{ width: '100%' }}
          value={filter ? filter.value : ''}
        >
          <option value="">{t('all')}</option>
          <option value={FlowMeterProductType.SingleTankProduct}>
            {t(`admin.flow_meter_types.singletankproduct`)}
          </option>
          <option value={FlowMeterProductType.ProductMixture}>{t(`admin.flow_meter_types.productmixture`)}</option>
          <option value={FlowMeterProductType.UnknownProductMixture}>
            {t(`admin.flow_meter_types.unknownproductmixture`)}
          </option>
          <option value={FlowMeterProductType.Unknown}>{t(`admin.flow_meter_types.unknown`)}</option>
        </select>
      )
    },
    {
      Header: t('admin.connected'),
      id: 'connected',
      accessor: (item: FlowMeter) => item.components.filter(c => c.bookkeepingEntryId).length > 0,
      show: true,
      width: 90,
      Cell: ({ value }) => (value ? <strong>{t('filter_active')}</strong> : <span>{t('filter_inactive')}</span>),
      filterMethod: (filter, row) => (filter.value !== '' ? row[filter.id] === (filter.value === 'true') : true),
      Filter: ({ filter, onChange }) => (
        <select
          onChange={event => onChange(event.target.value)}
          style={{ width: '100%' }}
          value={filter ? filter.value : ''}
        >
          <option value="" />
          <option value="true">{t('filter_active')}</option>
          <option value="false">{t('filter_inactive')}</option>
        </select>
      )
    },
    {
      Header: 'Json',
      id: 'json',
      accessor: 'originalJsonData',
      show: true
    }
  ];

  return (
    <>
      <AdminNavigation isEnabled={true} isLoading={isLoading} fetch={() => refetch()} />
      <AdminFiltersControlled value={dateFilter} onChange={setDateFilter} />
      <Table
        data={filteredItems}
        columns={columns}
        filterable={true}
        defaultSortColumn="receivedAt"
        defaultSortDesc={true}
        defaultPageSize={20}
        noDataText={t('entry.no_rows_bookkeeping_reporting')}
      />
    </>
  );
};

export default FlowMetersPage;
